import { PageBillingType } from '@mm/state/interfaces/connected-page';
import { assign, AssignMeta, createMachine, GuardMeta } from 'xstate';

export type NewPlans = 'Operator' | 'Startup' | 'Agency';

export type PlanCopy = {
  title: string;
  recommendedFor: string;
  features: {
    text: string;
    disabled?: boolean;
    availablePlan?: NewPlans;
  }[];
  callToAction: { text: string; linkText: string; link: string };
};

type PlanPrices = {
  [key in NewPlans]: {
    monthlyPrice: number;
    monthlyPricePaidYearly: number;
  };
};

export type UpgradeLandmineContext = {
  availablePlans?: NewPlans[];
  nextBillingType?: PageBillingType;
  persona?: 'smb' | 'marketer' | 'agency';
  planCopy?: PlanCopy;
  planPrices?: PlanPrices;
};

export enum UpgradeLandmineEvents {
  switchPlan = 'switchPlan',
  upgrade = 'upgrade',
  setPlanPrices = 'setPlanPrices',
  close = 'close',
}

type SwitchPlanEvent = {
  type: UpgradeLandmineEvents.switchPlan;
  plan: NewPlans;
};

type GotPlanPricesEvent = {
  type: UpgradeLandmineEvents.setPlanPrices;
  planPrices: PlanPrices;
};

export type UpgradeLandmineEventSchema =
  | SwitchPlanEvent
  | GotPlanPricesEvent
  | { type: UpgradeLandmineEvents.upgrade };

export enum UpgradeLandmineStates {
  init = 'init',
  choosePlan = 'choosePlan',
  Operator = 'Operator',
  Startup = 'Startup',
  Agency = 'Agency',
}

type UpgradeLandmineConextState =
  | {
      value: UpgradeLandmineStates.init;
      context: UpgradeLandmineContext & {
        persona?: string;
        availablePlans: undefined;
        planCopy: undefined;
        planPrices: undefined;
      };
    }
  | {
      value:
        | UpgradeLandmineStates.choosePlan
        | UpgradeLandmineStates.Operator
        | UpgradeLandmineStates.Startup
        | UpgradeLandmineStates.Agency;
      context: UpgradeLandmineContext & {
        persona?: string;
        availablePlans: NewPlans[];
        planCopy: PlanCopy;
        planPrices: PlanPrices;
      };
    };

type PlansCopy = { [key in NewPlans]: PlanCopy };
export const getPlansCopy = (november2021Pricing?: boolean): PlansCopy => ({
  [UpgradeLandmineStates.Operator]: {
    title: 'OPERATOR',
    recommendedFor: `MobileMonkey Operator Edition is perfect for SMBs`,
    features: [
      { text: '2,000 Send Credits' },
      { text: '1 User' },
      { text: 'All features in FREE, PLUS' },
      { text: 'SMS (US & Canada only)' },
      { text: 'Chat Blaster/Drip Campaigns' },
      { text: 'Custom Notifications' },
      { text: 'Remove MobileMonkey Branding' },
      { text: 'Unlimited Q&As' },
      { text: 'Business Hours' },
      { text: 'Advanced Website Chat Settings' },
      {
        text: 'Analytics',
        disabled: true,
        availablePlan: UpgradeLandmineStates.Startup,
      },
      {
        text: 'MobileMonkey API Connections/Zapier Integration',
        disabled: true,
        availablePlan: UpgradeLandmineStates.Startup,
      },
    ],
    callToAction: {
      text: 'Interested in a Hands-off Chatbot built for you by one of our Chat Marketing Experts?',
      linkText: 'Email support@mobilemonkey.com',
      link: 'mailto:support@mobilemonkey.com',
    },
  },
  [UpgradeLandmineStates.Startup]: {
    title: 'STARTUP',
    recommendedFor: `MobileMonkey Startup Edition is perfect for Marketers`,
    features: [
      {
        text: november2021Pricing ? '1,000 Send Credits' : '6,500 Send Credits',
      },
      { text: '1 User' },
      { text: `All features in ${UpgradeLandmineStates.Operator}, PLUS:` },
      { text: 'Facebook Ads' },
      { text: 'Analytics' },
      { text: 'MobileMonkey API Connections/Zapier Integration' },
      { text: 'Automation Rules' },
      { text: 'Live Chat Support' },
      {
        text: 'Bot Cloning/Template Sharing',
        disabled: true,
        availablePlan: UpgradeLandmineStates.Agency,
      },
      {
        text: 'Automated Lead Routing',
        disabled: true,
        availablePlan: UpgradeLandmineStates.Agency,
      },
    ],
    callToAction: {
      // tslint:disable-next-line: max-line-length
      text: `Get the most out of your ${UpgradeLandmineStates.Startup} subscription with Premier Consulting from one of our Chat Marketing experts!`,
      linkText: 'Email support@mobilemonkey.com to learn more',
      link: 'mailto:support@mobilemonkey.com',
    },
  },
  [UpgradeLandmineStates.Agency]: {
    title: 'AGENCY',
    recommendedFor: `MobileMonkey Agency Edition is perfect for Agencies`,
    features: [
      { text: '10 Facebook pages' },
      { text: '100,000 Send Credits' },
      { text: '1 User' },
      { text: `All features in ${UpgradeLandmineStates.Startup}, PLUS:` },
      { text: 'Bot Cloning & Template Sharing' },
      { text: 'Branded Sign In & Live Chat' },
      { text: 'Automated Lead Routing' },
    ],
    callToAction: {
      text: 'Need more than 10 accounts?',
      linkText: 'Email sales@mobilemonkey.com',
      link: 'mailto:sales@mobilemonkey.com',
    },
  },
});

export const getUpgradeLandmineMachine = (november2021Pricing?: boolean) =>
  createMachine<
    UpgradeLandmineContext,
    UpgradeLandmineEventSchema,
    UpgradeLandmineConextState
  >(
    {
      id: 'upgradeLandmineMachine',
      initial: UpgradeLandmineStates.init,
      states: {
        [UpgradeLandmineStates.init]: {
          on: {
            [UpgradeLandmineEvents.setPlanPrices]: {
              target: UpgradeLandmineStates.choosePlan,
              actions: ['assignPlanPrices'],
            },
          },
          exit: ['assignAvailablePlans'],
        },
        [UpgradeLandmineStates.choosePlan]: {
          on: {
            '': [
              {
                target: UpgradeLandmineStates.Operator,
                cond: {
                  type: 'personaRecommendedPlan',
                  plan: UpgradeLandmineStates.Operator,
                },
              },
              {
                target: UpgradeLandmineStates.Startup,
                cond: {
                  type: 'personaRecommendedPlan',
                  plan: UpgradeLandmineStates.Startup,
                },
              },
              {
                target: UpgradeLandmineStates.Agency,
                cond: {
                  type: 'personaRecommendedPlan',
                  plan: UpgradeLandmineStates.Agency,
                },
              },
            ],
          },
        },
        [UpgradeLandmineStates.Operator]: {
          onEntry: {
            type: 'assignPlanContext',
            plan: UpgradeLandmineStates.Operator,
          },
        },
        [UpgradeLandmineStates.Startup]: {
          onEntry: {
            type: 'assignPlanContext',
            plan: UpgradeLandmineStates.Startup,
          },
        },
        [UpgradeLandmineStates.Agency]: {
          onEntry: {
            type: 'assignPlanContext',
            plan: UpgradeLandmineStates.Agency,
          },
        },
      },
      on: {
        [UpgradeLandmineEvents.switchPlan]: [
          {
            target: UpgradeLandmineStates.Operator,
            cond: { type: 'planMatch', plan: 'Operator' },
          },
          {
            target: UpgradeLandmineStates.Startup,
            cond: { type: 'planMatch', plan: 'Startup' },
          },
          {
            target: UpgradeLandmineStates.Agency,
            cond: { type: 'planMatch', plan: 'Agency' },
          },
        ],
      },
    },
    {
      guards: {
        planMatch: (ctx, evt: SwitchPlanEvent, meta: PlanMatchGuardMeta) => {
          return evt.plan === meta.cond.plan;
        },
        personaRecommendedPlan: (ctx, evt, meta: PlanMatchGuardMeta) => {
          const personasList = ['smb', 'marketer', 'agency'].slice(
            3 - (ctx.availablePlans?.length || 0),
          );
          const personaIndex = ctx.persona
            ? personasList.indexOf(ctx.persona)
            : 0;

          return (
            ctx.availablePlans?.[personaIndex === -1 ? 0 : personaIndex] ===
            meta.cond.plan
          );
        },
      },
      actions: {
        assignPlanContext: assign((ctx, evt, meta: AssignPlanContextMeta) => {
          return {
            ...ctx,
            planCopy: {
              ...getPlansCopy(november2021Pricing)[meta.action.plan],
            },
          };
        }),
        // assignPlanPrices: assign((ctx, evt: GotPlanPricesEvent, meta) => {
        assignPlanPrices: assign((ctx, evt, meta) => {
          return { ...ctx, planPrices: (evt as GotPlanPricesEvent).planPrices };
        }),
        assignAvailablePlans: assign(ctx => {
          const billingTypesList = [
            PageBillingType.Pro,
            PageBillingType.ProUnicorn,
            PageBillingType.TeamLabel,
          ];
          const billingTypeIndex = billingTypesList.indexOf(
            ctx.nextBillingType || PageBillingType.Pro,
          );

          const availablePlans: NewPlans[] = [
            UpgradeLandmineStates.Operator,
            UpgradeLandmineStates.Startup,
            UpgradeLandmineStates.Agency,
          ];

          availablePlans.splice(0, billingTypeIndex);

          return {
            availablePlans,
          };
        }),
      },
    },
  );

type PlanMatchGuardMeta = GuardMeta<
  UpgradeLandmineContext,
  UpgradeLandmineEventSchema
> & {
  cond: {
    plan: string;
  };
};

type AssignPlanContextMeta = AssignMeta<
  UpgradeLandmineContext,
  UpgradeLandmineEventSchema
> & {
  action: {
    plan: NewPlans;
  };
};
